// @givmo/board — the documents a written consent's compensation block names, beside its text on the
// director's consent page (CBG-YR4-PAY-CONSENT; the backend's `board-compensation-decisions.md` §8.6).
//
// The block itself is in the consent's text, which the director reads before signing: whose pay, the
// documents the board relied on by name and fingerprint, how it obtained them. This card offers each
// of those files, opened through the director's own door, exactly as the bytes were when Board took
// the fingerprint the text names (a short-lived link to that version, minted per request and never
// stored). Reading a file is a read: no signing window.
//
// ⚠ A DIRECTOR SURFACE: it reaches only the director's port (`director/capacity-rail.test.ts`).

import { useMutation } from '@tanstack/react-query';
import type { BoardDirectorConsentCompensationOut } from '@givmo/api-client';
import { Card, CardBody, testIdProp, toast } from '@givmo/design-system';
import { useBoardDirectorOperations } from '../../board-runtime';
import { useDirectorFailureSentence } from '../../director/access-refusal';
import { compensationWords as w } from '../compensation/compensation-words';
import { displaySize } from '../packets/packets-decode';

const DOC_LINK =
  'min-w-0 text-left font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors [overflow-wrap:anywhere] hover:text-brand-700 focus-visible:underline';

export function DirectorConsentDocuments({
  orgRef,
  consentId,
  committee = false,
  decisions,
}: {
  orgRef: string;
  consentId: string;
  /** CBG-YR6: a committee's written consent, whose block names the committee. */
  committee?: boolean;
  decisions: readonly BoardDirectorConsentCompensationOut[];
}) {
  const operations = useBoardDirectorOperations();
  const failureSentence = useDirectorFailureSentence();

  const open = useMutation({
    mutationFn: async (input: { decisionId: string; documentId: string }) => {
      const { data } = await operations.directorGetBoardCompensationDocumentDownloadForDirector({
        path: {
          org_ref: orgRef,
          consent_id: consentId,
          decision_id: input.decisionId,
          document_id: input.documentId,
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => void window.open(link.download_url, '_blank', 'noopener,noreferrer'),
    onError: (caught) => toast.error(failureSentence(caught, w.openFailed)),
  });

  if (decisions.length === 0) return null;

  return (
    <Card testId="director-consent-documents">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {committee ? w.committeeDocumentsRelied : w.documentsRelied}
          </h2>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {w.documentsLine}
          </p>
        </div>
        {decisions.map((decision) => (
          <div
            key={decision.decision_id}
            className="flex flex-col gap-6"
            {...testIdProp(`director-consent-documents-${decision.decision_id}`)}
          >
            <span className="font-sans text-s13 font-w600 tracking-givmo text-text-secondary">
              {w.forPerson(decision.person_name, decision.position)}
            </span>
            <ul className="flex flex-col gap-4">
              {(decision.documents ?? []).map((document) => (
                <li key={document.id} className="flex min-w-0 flex-wrap items-baseline gap-x-8">
                  <button
                    type="button"
                    className={DOC_LINK}
                    disabled={open.isPending && open.variables?.documentId === document.id}
                    onClick={() =>
                      open.mutate({ decisionId: decision.decision_id, documentId: document.id })
                    }
                    {...testIdProp(`director-consent-document-${document.id}`)}
                  >
                    {document.file_name}
                  </button>
                  {displaySize(document.byte_size) !== null ? (
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {displaySize(document.byte_size)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
